import type { MessageId } from "../i18n/messages"
import type { useI18n } from "../i18n/useI18n"
import type { CommandError } from "./engine/match/state"

type Translate = ReturnType<typeof useI18n>["t"]

export type MatchSetupError =
  | { code: "DECK_NOT_FOUND"; deckId: string }
  | { code: "HERO_NOT_FOUND"; deckName: string }
  | { code: "CARD_NOT_FOUND"; cardId: string; deckName: string }
  | { code: "DECK_WRONG_CARD_COUNT"; deckName: string; count: number }

export type MatchSocketError = {
  code: string
  message: string
}

const MATCH_COMMAND_ERROR_MESSAGE_IDS: Record<string, MessageId> = {
  ATTACK_ALREADY_ATTACKED: "match.error.ATTACK_ALREADY_ATTACKED",
  ATTACK_BLOCKED_BY_GUARD: "match.error.ATTACK_BLOCKED_BY_GUARD",
  ATTACK_INVALID_TARGET_ZONE: "match.error.ATTACK_INVALID_TARGET_ZONE",
  ATTACK_NOT_IN_ATTACK_POSITION: "match.error.ATTACK_NOT_IN_ATTACK_POSITION",
  ATTACK_NO_TARGET_ID: "match.error.ATTACK_NO_TARGET_ID",
  ATTACK_SUPPORT_REQUIRES_BYPASS: "match.error.ATTACK_SUPPORT_REQUIRES_BYPASS",
  ATTACK_UNKNOWN_ATTACKER: "match.error.ATTACK_UNKNOWN_ATTACKER",
  ATTACK_UNKNOWN_TARGET: "match.error.ATTACK_UNKNOWN_TARGET",
  CONCEDE_MATCH_OVER: "match.error.CONCEDE_MATCH_OVER",
  END_TURN_WRONG_STEP: "match.error.END_TURN_WRONG_STEP",
  JAM_NO_TARGET: "match.error.JAM_NO_TARGET",
  JAM_UNKNOWN_TARGET: "match.error.JAM_UNKNOWN_TARGET",
  LEVEL_UP_HERO_DEF_NOT_FOUND: "match.error.LEVEL_UP_HERO_DEF_NOT_FOUND",
  LEVEL_UP_HERO_EXHAUSTED: "match.error.LEVEL_UP_HERO_EXHAUSTED",
  LEVEL_UP_INSUFFICIENT_ENERGY: "match.error.LEVEL_UP_INSUFFICIENT_ENERGY",
  LEVEL_UP_NO_HERO_INSTANCE: "match.error.LEVEL_UP_NO_HERO_INSTANCE",
  LEVEL_UP_WRONG_LEVEL: "match.error.LEVEL_UP_WRONG_LEVEL",
  MATCH_COMPLETED: "match.error.MATCH_COMPLETED",
  PLAY_CARD_ACTIVE_OCCUPIED: "match.error.PLAY_CARD_ACTIVE_OCCUPIED",
  PLAY_CARD_NOT_IN_HAND: "match.error.PLAY_CARD_NOT_IN_HAND",
  PLAY_CARD_REPLACE_NOT_IN_SUPPORT:
    "match.error.PLAY_CARD_REPLACE_NOT_IN_SUPPORT",
  PLAY_CARD_REPLACE_NOT_NEEDED: "match.error.PLAY_CARD_REPLACE_NOT_NEEDED",
  PLAY_CARD_SUPPORT_FULL: "match.error.PLAY_CARD_SUPPORT_FULL",
  PLAY_CARD_UNKNOWN_INSTANCE: "match.error.PLAY_CARD_UNKNOWN_INSTANCE",
  RECYCLE_INVALID_TARGET: "match.error.RECYCLE_INVALID_TARGET",
  RECYCLE_NO_TARGET: "match.error.RECYCLE_NO_TARGET",
  RECRUIT_INSUFFICIENT_ENERGY: "match.error.RECRUIT_INSUFFICIENT_ENERGY",
  RECRUIT_NO_SLOT_ID: "match.error.RECRUIT_NO_SLOT_ID",
  RECRUIT_SLOT_NOT_IN_ROW: "match.error.RECRUIT_SLOT_NOT_IN_ROW",
  RECRUIT_UNKNOWN_INSTANCE: "match.error.RECRUIT_UNKNOWN_INSTANCE",
  SALVAGE_NO_DISCARD: "match.error.SALVAGE_NO_DISCARD",
  SHIFT_NOT_IN_SUPPORT: "match.error.SHIFT_NOT_IN_SUPPORT",
  SHIFT_NO_ACTIVE: "match.error.SHIFT_NO_ACTIVE",
  SWAP_ALREADY_USED: "match.error.SWAP_ALREADY_USED",
  SWAP_NOT_IN_SUPPORT: "match.error.SWAP_NOT_IN_SUPPORT",
  UNKNOWN_COMMAND: "match.error.UNKNOWN_COMMAND",
  USE_ABILITY_ALLY_REQUIREMENT: "match.error.USE_ABILITY_ALLY_REQUIREMENT",
  USE_ABILITY_EXHAUSTED: "match.error.USE_ABILITY_EXHAUSTED",
  USE_ABILITY_JAMMED: "match.error.USE_ABILITY_JAMMED",
  USE_ABILITY_NOT_ACTIVATED: "match.error.USE_ABILITY_NOT_ACTIVATED",
  USE_ABILITY_NOT_OWNED: "match.error.USE_ABILITY_NOT_OWNED",
  USE_ABILITY_UNKNOWN_SKILL: "match.error.USE_ABILITY_UNKNOWN_SKILL",
  USE_ABILITY_UNKNOWN_SOURCE: "match.error.USE_ABILITY_UNKNOWN_SOURCE",
  WRONG_SEAT: "match.error.WRONG_SEAT",
  WRONG_TURN: "match.error.WRONG_TURN",
}

const MATCH_SOCKET_ERROR_MESSAGE_IDS: Record<string, MessageId> = {
  INVALID_JSON: "match.socket.INVALID_JSON",
  MATCH_NOT_INITIALIZED: "match.socket.MATCH_NOT_INITIALIZED",
  WS_CONNECTION_FAILED: "match.socket.WS_CONNECTION_FAILED",
}

export function getMatchSetupErrorLabel(
  t: Translate,
  error: MatchSetupError,
): string {
  switch (error.code) {
    case "DECK_NOT_FOUND":
      return t("match.setup.error.deckNotFound", { deckId: error.deckId })
    case "HERO_NOT_FOUND":
      return t("match.setup.error.heroNotFound", { deckName: error.deckName })
    case "CARD_NOT_FOUND":
      return t("match.setup.error.cardNotFound", {
        cardId: error.cardId,
        deckName: error.deckName,
      })
    case "DECK_WRONG_CARD_COUNT":
      return t("match.setup.error.deckWrongCardCount", {
        deckName: error.deckName,
        count: error.count,
      })
  }
}

export function getMatchCommandErrorLabel(
  t: Translate,
  error: Pick<CommandError, "code" | "message">,
): string {
  const messageId = MATCH_COMMAND_ERROR_MESSAGE_IDS[error.code]
  return messageId ? t(messageId) : error.message
}

export function getMatchSocketErrorLabel(
  t: Translate,
  error: MatchSocketError,
): string {
  const messageId =
    MATCH_SOCKET_ERROR_MESSAGE_IDS[error.code] ??
    MATCH_COMMAND_ERROR_MESSAGE_IDS[error.code]
  return messageId ? t(messageId) : error.message
}
